
import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Card, CardContent } from '@/components/ui/card';
import { useCartStore } from '@/store/useCartStore';
import { formatCurrency } from '@/lib/utils';
import TranslatedText from '@/TranslatedText';

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  currency: string;
  image: string;
  description: string;
  discount?: number; // Now getting discount from product data
  couponCode?: string; // Add coupon code support
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  currency,
  image,
  description,
  discount,
  couponCode
}) => {
  const { addItem } = useCartStore();
  
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      id,
      name,
      price: price.toString(),
      currency,
      image,
      quantity: 1,
      discount
    });
  };

  // Calculate discounted price if discount exists
  const discountedPrice = discount ? price * (1 - discount / 100) : price;
  
  return (
    <Card className="relative border-0 shadow-sm overflow-hidden group transition-all duration-300 hover:shadow-md hover:translate-y-[-2px] h-full w-full rounded-none">
      {/* Discount Badge */}
      {discount && discount > 0 && (
        <div className="absolute top-0 left-0 z-10">
          <Badge className="rounded-none text-white bg-red-600 hover:bg-red-700 text-[10px] font-bold py-0.5 px-1">
            -{discount}%
          </Badge>
        </div>
      )}
      
      {/* Coupon Badge */}
      {couponCode && (
        <div className="absolute top-0 right-0 z-10">
          <Badge className="rounded-none text-white bg-blue-600 hover:bg-blue-700 text-[10px] font-bold py-0.5 px-1">
            {couponCode}
          </Badge>
        </div>
      )}
      
      <Link to={`/product/${id}`} className="block">
        <div className="w-full overflow-hidden bg-white">
          <AspectRatio ratio={1 / 1} className="bg-white">
            <img src={image} alt={name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </AspectRatio>
        </div>
      </Link>
      
      <CardContent className="p-1.5 bg-white">
        <div className="flex flex-col">
          {discount && discount > 0 ? (
            <>
              <div className="text-red-600 font-bold text-xs mb-0.5">
                {formatCurrency(discountedPrice, currency)}
              </div>
              <div className="text-gray-400 text-[10px] line-through mb-0.5">
                {formatCurrency(price, currency)}
              </div>
            </>
          ) : (
            <div className="text-ahavex-blue font-bold text-xs mb-0.5">
              {formatCurrency(price, currency)}
            </div>
          )}
        </div>
        
        <Link to={`/product/${id}`} className="block">
          <h3 className="text-ahavex-blue font-semibold text-[11px] line-clamp-1 mb-0.5 min-h-[1rem] hover:text-ahavex-gold transition-colors duration-200">
            <TranslatedText text={name} />
          </h3>
        </Link>
        
        <p className="text-gray-600 text-[10px] line-clamp-1 mb-1.5">
          <TranslatedText text={description} />
        </p>
        
        <button onClick={handleAddToCart} className="w-full bg-ahavex-blue hover:bg-ahavex-darkblue text-white px-1.5 rounded-none flex items-center justify-center gap-1 transition-all duration-200 text-[10px] py-[6px]">
          <ShoppingCart size={10} />
          <TranslatedText text="Add to Cart" />
        </button>
      </CardContent>
    </Card>
  );
};

export default ProductCard;
